import React, { Fragment, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";

import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";

// Google Maps API Key
const GOOGLE_MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Helper: Format Date & Time
const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getRelativeTime = (timestamp) => {
  if (!timestamp) return "Never";
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const toDateInput = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Determine online status based on last sync timestamp
const getUserLiveStatus = (lastLocation) => {
  const time = lastLocation?.recordedAt || lastLocation?.updatedAt;
  if (!time) return { key: "OFFLINE", label: "Offline", variant: "secondary", color: "#6b7280" };
  const diffMins = (Date.now() - new Date(time).getTime()) / 60000;
  if (diffMins <= 15) return { key: "LIVE", label: "Live", variant: "success", color: "#10b981", pulse: true };
  if (diffMins <= 120) return { key: "RECENT", label: "Recent (<2h)", variant: "warning", color: "#f59e0b" };
  if (diffMins <= 720) return { key: "TODAY", label: "Today", variant: "info", color: "#3b82f6" };
  return { key: "IDLE", label: "Idle", variant: "secondary", color: "#9ca3af" };
};

// Script loader helper for Google Maps
const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      return resolve(window.google.maps);
    }
    const existing = document.getElementById("google-maps-script");
    if (existing) {
      if (window.google && window.google.maps) return resolve(window.google.maps);
      existing.addEventListener("load", () => resolve(window.google.maps));
      existing.addEventListener("error", (e) => reject(e));
      return;
    }
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

const StaffLiveTracking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.auth?.userDetails || {});

  const role = (user?.roleName || user?.role?.name || user?.role || "").toLowerCase().trim();
  const isAdmin = role === "admin" || role === "superadmin" || role === "super admin";

  // Data states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(30); // 0 (off), 15, 30, 60 seconds
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Selected User & Route states
  const [selectedUserId, setSelectedUserId] = useState(searchParams.get("staffId") || null);
  const [routeDateKey, setRouteDateKey] = useState(toDateInput());
  const [routePoints, setRoutePoints] = useState([]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [showRouteTrail, setShowRouteTrail] = useState(true);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, LIVE, TODAY

  // Map references
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]);
  const polylineRef = useRef(null);
  const routeOverlaysRef = useRef([]);
  const infoWindowRef = useRef(null);

  // -------------------------------------------------------------
  // Initialize Google Maps
  // -------------------------------------------------------------
  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsScript(GOOGLE_MAPS_KEY)
      .then((googleMaps) => {
        if (!isMounted || !mapContainerRef.current || mapInstanceRef.current) return;

        const map = new googleMaps.Map(mapContainerRef.current, {
          center: { lat: 20.5937, lng: 78.9629 }, // Center of India
          zoom: 5,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: googleMaps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: googleMaps.ControlPosition.TOP_RIGHT,
          },
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "greedy",
        });

        infoWindowRef.current = new googleMaps.InfoWindow();
        mapInstanceRef.current = map;
        setMapLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load Google Maps script:", err);
        swal("Google Maps Error", "Could not load Google Maps API. Please check your network/API key.", "error");
      });

    return () => {
      isMounted = false;
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      overlaysRef.current.forEach((ov) => ov.setMap(null));
      overlaysRef.current = [];
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      routeOverlaysRef.current.forEach((ov) => ov.setMap(null));
      routeOverlaysRef.current = [];
      mapInstanceRef.current = null;
    };
  }, []);

  // -------------------------------------------------------------
  // Fetch Latest Live Locations
  // -------------------------------------------------------------
  const fetchLiveLocations = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      else setRefreshing(true);

      const res = await api.get("/user-tracking/latest");
      const list = res.data?.data || [];
      setUsers(list);
      setLastSyncTime(new Date());

      // If initial load and staffId was provided in query param
      const staffIdParam = searchParams.get("staffId");
      if (staffIdParam && !selectedUserId) {
        setSelectedUserId(staffIdParam);
      } else if (!isAdmin && list.length > 0 && !selectedUserId) {
        // For non-admin employees, auto-select their own profile
        setSelectedUserId(list[0]._id);
      }
    } catch (err) {
      console.error("Failed to load live locations:", err);
      if (!isBackground) {
        swal("Error", "Failed to retrieve live locations", "error");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin, searchParams, selectedUserId]);

  useEffect(() => {
    fetchLiveLocations(false);
  }, [fetchLiveLocations]);

  // Auto-refresh interval (polling)
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;
    const intervalId = setInterval(() => {
      fetchLiveLocations(true);
    }, autoRefreshInterval * 1000);
    return () => clearInterval(intervalId);
  }, [autoRefreshInterval, fetchLiveLocations]);

  // -------------------------------------------------------------
  // Fetch Historical Route Points for Selected User & Date
  // -------------------------------------------------------------
  const fetchRouteHistory = useCallback(async (userId, dateStr) => {
    if (!userId || !showRouteTrail) {
      setRoutePoints([]);
      return;
    }
    try {
      setLoadingRoute(true);
      const res = await api.get(`/user-tracking/history/${userId}?date=${dateStr}`);
      const logs = res.data?.data || [];

      const validPoints = logs
        .filter((l) => (l.latitude || l.lat) && (l.longitude || l.lng))
        .map((l) => ({
          lat: Number(l.latitude || l.lat),
          lng: Number(l.longitude || l.lng),
          speed: l.speed || 0,
          accuracy: l.accuracy || 0,
          batteryLevel: l.batteryLevel ?? null,
          recordedAt: l.recordedAt || l.createdAt,
          address: l.address || "",
        }));

      setRoutePoints(validPoints);
    } catch (err) {
      console.warn("Failed to fetch route history:", err);
      setRoutePoints([]);
    } finally {
      setLoadingRoute(false);
    }
  }, [showRouteTrail]);

  useEffect(() => {
    if (selectedUserId) {
      fetchRouteHistory(selectedUserId, routeDateKey);
    } else {
      setRoutePoints([]);
    }
  }, [selectedUserId, routeDateKey, fetchRouteHistory]);

  // -------------------------------------------------------------
  // Filtered Staff List
  // -------------------------------------------------------------
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const q = searchQuery.toLowerCase().trim();
      const matchQuery =
        !q ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.mobile && u.mobile.includes(q)) ||
        (u.role?.name && u.role.name.toLowerCase().includes(q));

      if (!matchQuery) return false;

      const st = getUserLiveStatus(u.lastLocation);
      if (statusFilter === "LIVE") return st.key === "LIVE";
      if (statusFilter === "TODAY") return ["LIVE", "RECENT", "TODAY"].includes(st.key);

      return true;
    });
  }, [users, searchQuery, statusFilter]);

  // Currently selected user object
  const selectedUser = useMemo(() => {
    return users.find((u) => String(u._id) === String(selectedUserId)) || null;
  }, [users, selectedUserId]);

  // -------------------------------------------------------------
  // Render Custom Markers on Google Maps
  // -------------------------------------------------------------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || !window.google?.maps) return;

    // Clear old staff overlays
    overlaysRef.current.forEach((ov) => ov.setMap(null));
    overlaysRef.current = [];

    const google = window.google;
    const bounds = new google.maps.LatLngBounds();
    let validCount = 0;

    // Custom Overlay View for HTML Pulse Avatar Pin
    class CustomStaffOverlay extends google.maps.OverlayView {
      constructor(lat, lng, html, onClick) {
        super();
        this.position = new google.maps.LatLng(lat, lng);
        this.html = html;
        this.onClick = onClick;
        this.div = null;
      }

      onAdd() {
        this.div = document.createElement("div");
        this.div.style.position = "absolute";
        this.div.style.cursor = "pointer";
        this.div.style.zIndex = "10";
        this.div.innerHTML = this.html;

        if (this.onClick) {
          this.div.addEventListener("click", (e) => {
            e.stopPropagation();
            this.onClick();
          });
        }

        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(this.div);
      }

      draw() {
        const overlayProjection = this.getProjection();
        if (!overlayProjection || !this.div) return;
        const point = overlayProjection.fromLatLngToDivPixel(this.position);
        if (point) {
          this.div.style.left = point.x - 30 + "px";
          this.div.style.top = point.y - 48 + "px";
        }
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }
    }

    // If a user is focused/selected, only show that specific user on the map.
    // When no user is focused, show all filtered staff.
    const usersToDisplay = selectedUserId
      ? users.filter((u) => String(u._id) === String(selectedUserId))
      : filteredUsers;

    usersToDisplay.forEach((u) => {
      const loc = u.lastLocation;
      if (!loc || !loc.latitude || !loc.longitude) return;

      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      bounds.extend(new google.maps.LatLng(lat, lng));
      validCount += 1;

      const st = getUserLiveStatus(loc);
      const isSelected = String(u._id) === String(selectedUserId);
      const initials = (u.name || "U")
        .split(" ")
        .map((n) => n[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();

      const markerHtml = `
        <div class="live-pin-container ${isSelected ? "selected" : ""}">
          ${st.pulse ? '<div class="live-pin-pulse"></div>' : ""}
          <div class="live-pin-marker" style="border-color: ${st.color};">
            ${
              u.image
                ? `<img src="${getFileUrl(u.image)}" class="live-pin-avatar" alt="${u.name}" />`
                : `<div class="live-pin-initials">${initials}</div>`
            }
            <span class="live-pin-badge" style="background-color: ${st.color};"></span>
          </div>
          <div class="live-pin-label">${u.name?.split(" ")[0] || "Staff"}</div>
        </div>
      `;

      // Build InfoWindow popup content
      const batteryPercent =
        loc.batteryLevel !== null && loc.batteryLevel !== undefined
          ? Math.round(Number(loc.batteryLevel) <= 1 ? loc.batteryLevel * 100 : loc.batteryLevel)
          : null;

      const speedKmH = loc.speed ? Math.round(Number(loc.speed) * 3.6) : 0;

      const popupHtml = `
        <div class="live-map-popup" style="max-width:240px;font-family:inherit;padding:4px;">
          <div class="d-flex align-items-center gap-2 mb-2">
            ${
              u.image
                ? `<img src="${getFileUrl(u.image)}" style="width:36px;height:36px;border-radius:50%;object-fit:cover;" />`
                : `<div class="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold" style="width:36px;height:36px;font-size:13px;">${initials}</div>`
            }
            <div>
              <strong style="font-size:14px;display:block;">${u.name || "Staff"}</strong>
              <span class="badge" style="background-color:${st.color};font-size:10px;">${st.label}</span>
              <span class="text-muted small ms-1" style="font-size:10px;">• ${getRelativeTime(loc.recordedAt || loc.updatedAt)}</span>
            </div>
          </div>
          <div class="small text-muted mb-2" style="font-size:11px;line-height:1.4;">
            ${loc.address ? `<div class="mb-1 text-dark"><strong>Address:</strong> ${loc.address}</div>` : ""}
            <div><strong>Coordinates:</strong> ${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
            ${speedKmH > 0 ? `<div><strong>Speed:</strong> ${speedKmH} km/h</div>` : ""}
            ${batteryPercent !== null ? `<div><strong>Battery:</strong> ${batteryPercent}%</div>` : ""}
            ${loc.accuracy ? `<div><strong>Accuracy:</strong> ±${Math.round(loc.accuracy)}m</div>` : ""}
          </div>
          <div class="d-flex gap-1 mt-2">
            <a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noreferrer" class="btn btn-xs btn-primary text-white flex-grow-1" style="font-size:11px;padding:4px 8px;text-decoration:none;border-radius:4px;text-align:center;">
              Open in Google Maps
            </a>
            ${
              u.mobile
                ? `<a href="tel:${u.mobile}" class="btn btn-xs btn-outline-success" style="font-size:11px;padding:4px 8px;text-decoration:none;border-radius:4px;">Call</a>`
                : ""
            }
          </div>
        </div>
      `;

      const overlay = new CustomStaffOverlay(lat, lng, markerHtml, () => {
        setSelectedUserId(u._id);
        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(popupHtml);
          infoWindowRef.current.setPosition({ lat, lng });
          infoWindowRef.current.open(map);
        }
      });

      overlay.setMap(map);
      overlaysRef.current.push(overlay);

      // If this is the selected user, auto open InfoWindow
      if (isSelected && infoWindowRef.current) {
        infoWindowRef.current.setContent(popupHtml);
        infoWindowRef.current.setPosition({ lat, lng });
        infoWindowRef.current.open(map);
      }
    });

    // Zoom handling
    if (selectedUser?.lastLocation?.latitude && selectedUser?.lastLocation?.longitude) {
      map.panTo({
        lat: Number(selectedUser.lastLocation.latitude),
        lng: Number(selectedUser.lastLocation.longitude),
      });
      if (!showRouteTrail || routePoints.length === 0) {
        map.setZoom(16);
      }
    } else if (validCount > 0 && !selectedUserId) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      // Avoid over-zooming if only 1 point
      if (validCount === 1) {
        map.setZoom(14);
      }
    }
  }, [filteredUsers, users, selectedUserId, selectedUser, mapLoaded, showRouteTrail, routePoints.length]);

  // -------------------------------------------------------------
  // Render Route Trail (Polyline + Start/End Markers) on Google Maps
  // -------------------------------------------------------------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapLoaded || !window.google?.maps) return;

    const google = window.google;

    // Clean up previous polyline & route markers
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    routeOverlaysRef.current.forEach((ov) => ov.setMap(null));
    routeOverlaysRef.current = [];

    if (!showRouteTrail || routePoints.length === 0) return;

    const path = routePoints.map((p) => ({ lat: p.lat, lng: p.lng }));

    // Draw route polyline
    const polyline = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#2563eb",
      strokeOpacity: 0.85,
      strokeWeight: 5,
      map,
    });
    polylineRef.current = polyline;

    class RouteMarkerOverlay extends google.maps.OverlayView {
      constructor(lat, lng, html) {
        super();
        this.position = new google.maps.LatLng(lat, lng);
        this.html = html;
        this.div = null;
      }
      onAdd() {
        this.div = document.createElement("div");
        this.div.style.position = "absolute";
        this.div.style.zIndex = "5";
        this.div.innerHTML = this.html;
        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(this.div);
      }
      draw() {
        const overlayProjection = this.getProjection();
        if (!overlayProjection || !this.div) return;
        const point = overlayProjection.fromLatLngToDivPixel(this.position);
        if (point) {
          this.div.style.left = point.x - 20 + "px";
          this.div.style.top = point.y - 12 + "px";
        }
      }
      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }
    }

    // Start point marker (Green flag/dot)
    const startPt = routePoints[0];
    const startOverlay = new RouteMarkerOverlay(
      startPt.lat,
      startPt.lng,
      `
        <div class="route-point-marker start">
          <div class="route-point-dot bg-success"></div>
          <span class="route-point-tag bg-success text-white">START</span>
        </div>
      `
    );
    startOverlay.setMap(map);
    routeOverlaysRef.current.push(startOverlay);

    // End / Latest point marker (Blue flag/dot)
    if (routePoints.length > 1) {
      const endPt = routePoints[routePoints.length - 1];
      const endOverlay = new RouteMarkerOverlay(
        endPt.lat,
        endPt.lng,
        `
          <div class="route-point-marker end">
            <div class="route-point-dot bg-primary"></div>
            <span class="route-point-tag bg-primary text-white">LATEST</span>
          </div>
        `
      );
      endOverlay.setMap(map);
      routeOverlaysRef.current.push(endOverlay);
    }

    // Zoom to fit route
    const routeBounds = new google.maps.LatLngBounds();
    path.forEach((pt) => routeBounds.extend(new google.maps.LatLng(pt.lat, pt.lng)));
    map.fitBounds(routeBounds, { top: 60, right: 60, bottom: 60, left: 60 });
  }, [routePoints, showRouteTrail, mapLoaded]);

  // Show and center on all markers (resets single-user focus)
  const fitAllStaff = () => {
    setSelectedUserId(null);
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps) return;
    const google = window.google;
    const bounds = new google.maps.LatLngBounds();
    let count = 0;
    users.forEach((u) => {
      if (u.lastLocation?.latitude && u.lastLocation?.longitude) {
        bounds.extend(new google.maps.LatLng(Number(u.lastLocation.latitude), Number(u.lastLocation.longitude)));
        count++;
      }
    });
    if (count > 0) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      if (count === 1) map.setZoom(14);
    }
  };

  // Center on selected staff
  const centerOnStaff = (u) => {
    setSelectedUserId(u._id);
    const map = mapInstanceRef.current;
    if (map && u.lastLocation?.latitude && u.lastLocation?.longitude) {
      map.panTo({
        lat: Number(u.lastLocation.latitude),
        lng: Number(u.lastLocation.longitude),
      });
      map.setZoom(16);
    }
  };

  // KPIs
  const stats = useMemo(() => {
    let liveCount = 0;
    let todayCount = 0;
    users.forEach((u) => {
      const st = getUserLiveStatus(u.lastLocation);
      if (st.key === "LIVE") liveCount += 1;
      if (["LIVE", "RECENT", "TODAY"].includes(st.key)) todayCount += 1;
    });
    return {
      total: users.length,
      live: liveCount,
      today: todayCount,
    };
  }, [users]);

  return (
    <Fragment>
      <PageTitle activeMenu="Live Tracking" motherMenu="Attendance" />

      {/* Top Header Bar */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-0 d-flex align-items-center gap-2">
            Staff Live Attendance & GPS Tracking
            {isAdmin ? (
              <Badge bg="primary" style={{ fontSize: 12 }}>
                Admin Fleet View
              </Badge>
            ) : (
              <Badge bg="success" style={{ fontSize: 12 }}>
                My Live Tracker
              </Badge>
            )}
            <Badge bg="light" text="dark" className="border ms-1" style={{ fontSize: 11 }}>
              <i className="fab fa-google text-danger me-1"></i> Google Maps
            </Badge>
          </h3>
          <div className="text-muted small">
            {isAdmin
              ? "Monitor real-time GPS locations, route histories, and punch positions for all staff with Google Maps"
              : "Track your real-time GPS location and view your travel route logs for today"}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate("/staff-attendance")}>
            Staff Attendance
          </Button>
          <Button variant="outline-primary" size="sm" onClick={() => navigate("/staff-salary")}>
            Attendance & Salary
          </Button>

          {/* Auto Refresh Select */}
          <Form.Select
            size="sm"
            style={{ width: "auto", fontSize: 12 }}
            value={autoRefreshInterval}
            onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
          >
            <option value={0}>Auto-refresh: Off</option>
            <option value={15}>Auto-refresh: 15s</option>
            <option value={30}>Auto-refresh: 30s</option>
            <option value={60}>Auto-refresh: 60s</option>
          </Form.Select>

          {/* Manual Refresh Button */}
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => fetchLiveLocations(true)}
            disabled={refreshing}
            className="d-flex align-items-center gap-1"
          >
            {refreshing ? <Spinner animation="border" size="sm" /> : <span>↻</span>}
            <span>{refreshing ? "Updating..." : "Refresh"}</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <Row className="g-3 mb-3">
        <Col xs={6} md={3}>
          <Card className="shadow-sm border-0 border-start border-4 border-primary h-100">
            <Card.Body className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: 11 }}>
                  Total Staff
                </div>
                <h3 className="mb-0 fw-bold">{stats.total}</h3>
              </div>
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center text-primary"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <i className="fa fa-users" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={3}>
          <Card className="shadow-sm border-0 border-start border-4 border-success h-100">
            <Card.Body className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: 11 }}>
                  Live Now (&lt;15m)
                </div>
                <h3 className="mb-0 fw-bold text-success">{stats.live}</h3>
              </div>
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center text-success"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <i className="fa fa-dot-circle" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={3}>
          <Card className="shadow-sm border-0 border-start border-4 border-info h-100">
            <Card.Body className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: 11 }}>
                  Active Today
                </div>
                <h3 className="mb-0 fw-bold text-info">{stats.today}</h3>
              </div>
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center text-info"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <i className="fa fa-calendar-check" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={3}>
          <Card className="shadow-sm border-0 border-start border-4 border-warning h-100">
            <Card.Body className="p-3 d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: 11 }}>
                  Last Server Sync
                </div>
                <h5 className="mb-0 fw-bold" style={{ fontSize: 14 }}>
                  {lastSyncTime ? formatTime(lastSyncTime) : "-"}
                </h5>
                <small className="text-muted" style={{ fontSize: 10 }}>
                  {lastSyncTime ? `${getRelativeTime(lastSyncTime)}` : "Connecting..."}
                </small>
              </div>
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center text-warning"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <i className="fa fa-satellite-dish" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Layout: Left Sidebar + Right Map */}
      <Row className="g-3">
        {/* Left Column: Staff Directory */}
        <Col xs={12} lg={4} xl={3}>
          <Card className="shadow-sm border-0 overflow-hidden d-flex flex-column" style={{ height: "100%", minHeight: 650 }}>
            {/* Search & Filter Header */}
            <div className="p-3 border-bottom bg-white d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold small text-uppercase text-muted" style={{ fontSize: 11 }}>
                  Staff Members ({filteredUsers.length})
                </span>
                {statusFilter !== "ALL" && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none small text-muted"
                    style={{ fontSize: 11 }}
                    onClick={() => setStatusFilter("ALL")}
                  >
                    Reset Filter
                  </Button>
                )}
              </div>

              {/* Search Bar */}
              <div className="position-relative">
                <Form.Control
                  size="sm"
                  placeholder="Search name, role, mobile..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: "32px", fontSize: 12, height: "34px", borderRadius: "6px" }}
                />
                <i
                  className="fa fa-search position-absolute text-muted"
                  style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "12px", pointerEvents: "none" }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm position-absolute text-muted p-0 text-decoration-none"
                    style={{ right: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "12px" }}
                    onClick={() => setSearchQuery("")}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Status Filter Tabs */}
              <div className="d-flex gap-1 p-1 bg-light rounded border">
                <Button
                  size="sm"
                  variant={statusFilter === "ALL" ? "primary" : "light"}
                  className={`flex-grow-1 py-1 border-0 ${statusFilter === "ALL" ? "shadow-sm text-white" : "text-secondary bg-transparent"}`}
                  style={{ fontSize: 11, fontWeight: 600 }}
                  onClick={() => setStatusFilter("ALL")}
                >
                  All ({users.length})
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === "LIVE" ? "success" : "light"}
                  className={`flex-grow-1 py-1 border-0 ${statusFilter === "LIVE" ? "shadow-sm text-white" : "text-secondary bg-transparent"}`}
                  style={{ fontSize: 11, fontWeight: 600 }}
                  onClick={() => setStatusFilter("LIVE")}
                >
                  Live ({stats.live})
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === "TODAY" ? "info" : "light"}
                  className={`flex-grow-1 py-1 border-0 ${statusFilter === "TODAY" ? "shadow-sm text-white" : "text-secondary bg-transparent"}`}
                  style={{ fontSize: 11, fontWeight: 600 }}
                  onClick={() => setStatusFilter("TODAY")}
                >
                  Today ({stats.today})
                </Button>
              </div>
            </div>

            <Card.Body className="p-0 flex-grow-1 overflow-auto" style={{ maxHeight: 580 }}>
              {loading ? (
                <div className="text-center py-5 text-muted">
                  <Spinner animation="border" size="sm" className="mb-2" />
                  <div>Loading live staff data...</div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-5 text-muted px-3">
                  <div className="mb-1 text-muted" style={{ fontSize: 24 }}>
                    <i className="fa fa-map-marker" />
                  </div>
                  <p className="mb-0 small">No staff matching current filters</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {filteredUsers.map((u) => {
                    const loc = u.lastLocation;
                    const st = getUserLiveStatus(loc);
                    const isSelected = String(u._id) === String(selectedUserId);
                    const hasCoords = loc?.latitude && loc?.longitude;
                    const initials = (u.name || "U")
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .substring(0, 2)
                      .toUpperCase();

                    return (
                      <div
                        key={u._id}
                        className={`list-group-item list-group-item-action p-3 border-bottom ${isSelected ? "bg-light border-start border-3 border-primary" : ""
                          }`}
                        style={{ cursor: "pointer", transition: "background 0.15s" }}
                        onClick={() => centerOnStaff(u)}
                      >
                        <div className="d-flex align-items-start gap-2">
                          {/* Avatar */}
                          <div className="position-relative">
                            {u.image ? (
                              <img
                                src={getFileUrl(u.image)}
                                alt={u.name}
                                style={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: `2px solid ${st.color}`,
                                }}
                              />
                            ) : (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                style={{
                                  width: 42,
                                  height: 42,
                                  backgroundColor: "#4f46e5",
                                  fontSize: 14,
                                  border: `2px solid ${st.color}`,
                                }}
                              >
                                {initials}
                              </div>
                            )}
                            <span
                              className="position-absolute bottom-0 end-0 rounded-circle border border-white"
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: st.color,
                              }}
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex justify-content-between align-items-center mb-0.5">
                              <strong className="text-dark text-truncate" style={{ fontSize: 13 }}>
                                {u.name}
                              </strong>
                              <Badge bg={st.variant} style={{ fontSize: 9 }}>
                                {st.label}
                              </Badge>
                            </div>

                            <div className="text-muted small text-truncate mb-1" style={{ fontSize: 11 }}>
                              {u.role?.name || u.email || "-"}
                            </div>

                            {hasCoords ? (
                              <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: 10 }}>
                                <span>{getRelativeTime(loc.recordedAt || loc.updatedAt)}</span>
                                {loc.speed ? (
                                  <span className="text-primary fw-semibold">
                                    {Math.round(Number(loc.speed) * 3.6)} km/h
                                  </span>
                                ) : null}
                                {loc.batteryLevel !== null && loc.batteryLevel !== undefined ? (
                                  <span>{Math.round(Number(loc.batteryLevel) <= 1 ? loc.batteryLevel * 100 : loc.batteryLevel)}%</span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-muted small" style={{ fontSize: 10 }}>No GPS recorded</span>
                            )}
                          </div>
                        </div>

                        {/* Address Snippet */}
                        {loc?.address && (
                          <div className="mt-1 small text-muted text-truncate" style={{ fontSize: 10 }}>
                            {loc.address}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column: Interactive Map & Telemetry Panel */}
        <Col xs={12} lg={8} xl={9} className="d-flex flex-column">
          <Card className="shadow-sm border-0 overflow-hidden d-flex flex-column flex-grow-1" style={{ height: "100%", minHeight: 650 }}>
            {/* Map Action Toolbar */}
            <div className="bg-white border-bottom p-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  variant={selectedUser ? "outline-secondary" : "outline-dark"}
                  size="sm"
                  onClick={fitAllStaff}
                  title="Show all staff markers on map"
                >
                  <i className="fa fa-users me-1" />
                  Show All Staff
                </Button>

                {selectedUser && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => centerOnStaff(selectedUser)}
                    title="Center on selected staff"
                    className="d-flex align-items-center gap-1"
                  >
                    <i className="fa fa-user me-1" />
                    <span>Focusing: {selectedUser.name?.split(" ")[0]}</span>
                    <span
                      className="badge bg-light text-dark ms-1"
                      style={{ cursor: "pointer", fontSize: 10, padding: "2px 5px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUserId(null);
                        if (infoWindowRef.current) infoWindowRef.current.close();
                      }}
                      title="Clear focus (Show all staff on map)"
                    >
                      ✕
                    </span>
                  </Button>
                )}

                {/* Route Trail Toggle */}
                <Form.Check
                  type="switch"
                  id="toggle-route-trail"
                  label="Show Route Trail"
                  checked={showRouteTrail}
                  onChange={(e) => setShowRouteTrail(e.target.checked)}
                  className="ms-2 small fw-semibold"
                />
              </div>

              {/* Date Selector for Route History */}
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 small text-muted text-nowrap">Route Date:</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={routeDateKey}
                  onChange={(e) => setRouteDateKey(e.target.value)}
                  style={{ width: 145, fontSize: 12 }}
                />
                {loadingRoute && <Spinner animation="border" size="sm" className="text-primary" />}
              </div>
            </div>

            {/* Map Container */}
            <div className="position-relative flex-grow-1" style={{ minHeight: 560, width: "100%" }}>
              {!mapLoaded && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light"
                  style={{ zIndex: 2 }}
                >
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2 small text-muted fw-semibold">Loading Google Maps...</div>
                  </div>
                </div>
              )}

              <div
                ref={mapContainerRef}
                id="staff-live-map"
                style={{
                  height: "100%",
                  minHeight: 560,
                  width: "100%",
                  zIndex: 1,
                }}
              />

              {/* Float Overlay: Selected Staff Telemetry Drawer */}
              {selectedUser && (
                <div
                  className="live-telemetry-drawer position-absolute bottom-0 start-0 end-0 m-3 p-3 bg-white rounded-3 shadow-lg border"
                  style={{ zIndex: 1000 }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <div className="d-flex align-items-center gap-3">
                      {selectedUser.image ? (
                        <img
                          src={getFileUrl(selectedUser.image)}
                          alt={selectedUser.name}
                          style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                          style={{ width: 48, height: 48, fontSize: 16 }}
                        >
                          {(selectedUser.name || "U").substring(0, 2).toUpperCase()}
                        </div>
                      )}

                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0 fw-bold">{selectedUser.name}</h5>
                          <Badge bg={getUserLiveStatus(selectedUser.lastLocation).variant}>
                            {getUserLiveStatus(selectedUser.lastLocation).label}
                          </Badge>
                        </div>
                        <div className="text-muted small">
                          {selectedUser.role?.name || "Staff"} • {selectedUser.email || "-"} • {selectedUser.mobile || "-"}
                        </div>
                      </div>
                    </div>

                    {/* Telemetry Stats Pills */}
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <div className="p-2 px-3 rounded bg-light border text-center">
                        <div className="text-muted" style={{ fontSize: 10 }}>SPEED</div>
                        <strong style={{ fontSize: 13 }}>
                          {selectedUser.lastLocation?.speed
                            ? `${Math.round(Number(selectedUser.lastLocation.speed) * 3.6)} km/h`
                            : "0 km/h"}
                        </strong>
                      </div>

                      <div className="p-2 px-3 rounded bg-light border text-center">
                        <div className="text-muted" style={{ fontSize: 10 }}>BATTERY</div>
                        <strong style={{ fontSize: 13 }}>
                          {selectedUser.lastLocation?.batteryLevel !== null && selectedUser.lastLocation?.batteryLevel !== undefined
                            ? `${Math.round(
                              Number(selectedUser.lastLocation.batteryLevel) <= 1
                                ? selectedUser.lastLocation.batteryLevel * 100
                                : selectedUser.lastLocation.batteryLevel,
                            )}%`
                            : "-"}
                        </strong>
                      </div>

                      <div className="p-2 px-3 rounded bg-light border text-center">
                        <div className="text-muted" style={{ fontSize: 10 }}>ROUTE LOGS</div>
                        <strong style={{ fontSize: 13 }}>
                          {routePoints.length} points
                        </strong>
                      </div>

                      {/* External Actions */}
                      {selectedUser.lastLocation?.latitude && selectedUser.lastLocation?.longitude && (
                        <a
                          href={`https://www.google.com/maps?q=${selectedUser.lastLocation.latitude},${selectedUser.lastLocation.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                        >
                          Google Maps
                        </a>
                      )}

                      {selectedUser.mobile && (
                        <a href={`tel:${selectedUser.mobile}`} className="btn btn-sm btn-outline-success">
                          Call
                        </a>
                      )}

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedUserId(null);
                          if (infoWindowRef.current) infoWindowRef.current.close();
                        }}
                        title="Close details and show all staff on map"
                      >
                        ✕ Close (Show All)
                      </Button>
                    </div>
                  </div>

                  {/* Current Address & Updated Time */}
                  {selectedUser.lastLocation?.address && (
                    <div className="mt-2 small text-muted pt-2 border-top d-flex justify-content-between flex-wrap gap-2">
                      <div>
                        <strong>Current Location:</strong> {selectedUser.lastLocation.address}
                      </div>
                      <div>
                        <strong>Last Synced:</strong> {formatDate(selectedUser.lastLocation.recordedAt || selectedUser.lastLocation.updatedAt)}{" "}
                        {formatTime(selectedUser.lastLocation.recordedAt || selectedUser.lastLocation.updatedAt)} (
                        {getRelativeTime(selectedUser.lastLocation.recordedAt || selectedUser.lastLocation.updatedAt)})
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default StaffLiveTracking;
